import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

import { verifyTrainingCertificate } from '../../services/trainingService';
import { formatDate } from '../../utils/Utils';

const Shell = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #F0F9FF, #ECFEFF);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  max-width: 520px;
  width: 100%;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 66, 66, 0.10);
`;

const Badge = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $state }) => (
    $state === 'valid' ? '#D1FAE5' : $state === 'expired' ? '#FEF3C7' : '#FEE2E2'
  )};
  color: ${({ $state }) => (
    $state === 'valid' ? '#065F46' : $state === 'expired' ? '#92400E' : '#991B1B'
  )};
`;

const Title = styled.h1`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 22px;
  text-align: center;
  margin: 0 0 6px;
  color: #0F172A;
`;

const Sub = styled.div`
  font-size: 13px;
  text-align: center;
  color: #64748B;
  margin-bottom: 20px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #F1F5F9;
  font-size: 14px;
  &:last-child { border-bottom: none; }
`;

const Label = styled.span`color: #64748B;`;
const Val = styled.span`font-weight: 600; color: #0F172A;`;

export default function VerifyCertificatePage() {
  const { t, i18n } = useTranslation();
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await verifyTrainingCertificate(code);
        if (!cancelled) setData(res?.data?.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const state = !data ? 'invalid' : !data.valid ? 'invalid' : data.expired ? 'expired' : 'valid';

  return (
    <Shell>
      <Card>
        {loading ? (
          <Sub>{t('common.loading')}</Sub>
        ) : (
          <>
            <Badge $state={state}>
              {state === 'valid' ? <CheckCircle2 size={44} />
                : state === 'expired' ? <AlertTriangle size={44} />
                : <XCircle size={44} />}
            </Badge>
            <Title>
              {state === 'valid' ? t('training.verify.validTitle')
                : state === 'expired' ? t('training.verify.expiredTitle')
                : t('training.verify.invalidTitle')}
            </Title>
            <Sub>
              {state === 'valid' ? t('training.verify.validSub')
                : state === 'expired' ? t('training.verify.expiredSub')
                : t('training.verify.invalidSub')}
            </Sub>

            {data?.valid && (
              <div>
                <Row><Label>{t('training.certificateNumber')}</Label><Val>{data.certificateNumber}</Val></Row>
                <Row><Label>{t('training.learner')}</Label><Val>{data.learnerName || '-'}</Val></Row>
                <Row><Label>{t('training.title')}</Label><Val>{data.trainingTitle || '-'}</Val></Row>
                {data.tenantName && <Row><Label>{t('training.verify.issuer')}</Label><Val>{data.tenantName}</Val></Row>}
                <Row><Label>{t('training.certificateIssuedAt')}</Label><Val>{formatDate(data.issuedAt, i18n.language)}</Val></Row>
                {data.expiresAt && (
                  <Row><Label>{t('training.verify.validUntil')}</Label><Val>{formatDate(data.expiresAt, i18n.language)}</Val></Row>
                )}
              </div>
            )}
          </>
        )}
      </Card>
    </Shell>
  );
}
